import axios, { AxiosRequestConfig } from "axios";
import { getAuth } from "../../auth";
import { apiUrl } from "../../../utils/api";
import {
  BraceletStatus,
  OperatorStatus,
  PaymentMethod,
  TransactionStatus,
  WithdrawalType,
} from "./caixa.utils";

const base = () => `${apiUrl()}/caixa-financeiro`;

function headers(operation: string) {
  const auth = getAuth();
  return {
    Authorization: `Bearer ${auth?.api_token}`,
    operation,
  };
}

function client(operation: string): AxiosRequestConfig {
  return { headers: headers(operation) };
}

export interface PaginatedResponse<T> {
  page: number;
  amount: number;
  total: number;
  data: T[];
}

export interface CaixaOperator {
  _id: number;
  _id_user?: number;
  name: string;
  login: string;
  shift?: string;
  commission_pct: number;
  status: OperatorStatus;
}

export interface CaixaSession {
  _id: number;
  _id_operator: number;
  status: "aberto" | "fechado";
  opening_fund: number;
  closing_amount?: number;
  opened_at: string;
  closed_at?: string;
  notes?: string;
  operator?: CaixaOperator;
}

export interface CaixaTransactionItem {
  _id?: number;
  barcode: string;
  product_name: string;
  sector: string;
  unit_price: number;
  quantity: number;
  total_value?: number;
}

export interface CaixaPayment {
  _id?: number;
  _id_session?: number;
  _id_transaction?: number;
  payment_method: PaymentMethod;
  amount: number;
  card_brand?: string;
  authorization_code?: string;
  fee?: number;
  net_amount?: number;
  transaction_number?: string;
  status?: string;
  created_at?: string;
  due_date?: string;
  invoice_url?: string;
  billing_type?: string;
  payment_status?: string;
}

export interface CaixaTransaction {
  _id: number;
  _id_session: number;
  _id_employee: number;
  _id_client?: number;
  client_name: string;
  consumption_date: string;
  sector_description: string;
  total_value: number;
  paid_value: number;
  status: TransactionStatus;
  items?: CaixaTransactionItem[];
  payments?: CaixaPayment[];
}

export interface CaixaWithdrawal {
  _id: number;
  _id_session: number;
  _id_operator: number;
  type: WithdrawalType;
  description: string;
  amount: number;
  movement_date?: string;
}

export interface CaixaBracelet {
  _id: number;
  code: string;
  _id_client?: number;
  client_name: string;
  initial_balance: number;
  current_balance: number;
  status: BraceletStatus;
}

export interface CaixaCommission {
  _id: number;
  _id_operator: number;
  operator_name?: string;
  _id_transaction?: number;
  commission_pct: number;
  base_amount: number;
  commission_amount: number;
  created_at?: string;
}

export interface CaixaAuditLog {
  _id: number;
  entity: string;
  action: string;
  _id_entity?: number;
  _id_user?: number;
  _id_operator?: number;
  description?: string;
  previous_data?: unknown;
  new_data?: unknown;
  created_at: string;
}

export interface CaixaDashboard {
  open_session?: CaixaSession | null;
  transactions_count: number;
  total_sales: number;
  payments_by_method: { method: PaymentMethod; total: number }[];
  sangrias_total: number;
  suprimentos_total: number;
  active_operators: number;
}

function num(row: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null) return Number(row[key]);
  }
  return 0;
}

function str(row: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null) return String(row[key]);
  }
  return "";
}

export function normalizeOperator(raw: unknown): CaixaOperator {
  const row = (raw as Record<string, unknown>) || {};
  return {
    _id: num(row, "_id", "_id_operador"),
    _id_user: num(row, "_id_user", "_id_usuario") || undefined,
    name: str(row, "name", "nome"),
    login: str(row, "login"),
    shift: str(row, "shift", "turno") || undefined,
    commission_pct: num(row, "commission_pct", "comissao_pct"),
    status: (str(row, "status") || "ativo") as OperatorStatus,
  };
}

export function normalizePayment(raw: unknown): CaixaPayment {
  const pay = (raw as Record<string, unknown>) || {};
  return {
    _id: num(pay, "_id", "_id_pagamento") || undefined,
    _id_session: num(pay, "_id_session", "_id_sessao") || undefined,
    _id_transaction: num(pay, "_id_transaction", "_id_transacao") || undefined,
    payment_method: str(pay, "payment_method", "forma_pagamento") as PaymentMethod,
    amount: num(pay, "amount", "valor"),
    card_brand: str(pay, "card_brand", "bandeira_cartao", "bandeira") || undefined,
    authorization_code: str(pay, "authorization_code", "codigo_autorizacao") || undefined,
    fee: num(pay, "fee", "taxa") || undefined,
    net_amount: num(pay, "net_amount", "valor_liquido") || undefined,
    transaction_number: str(pay, "transaction_number", "numero_transacao") || undefined,
    status: str(pay, "status") || undefined,
    created_at: str(pay, "created_at", "criado_em") || undefined,
    due_date: str(pay, "due_date", "vencimento", "data_vencimento") || undefined,
    invoice_url: str(pay, "invoice_url", "url_fatura") || undefined,
    billing_type: str(pay, "billing_type", "tipo_cobranca") || undefined,
    payment_status: str(pay, "payment_status", "status_pagamento") || undefined,
  };
}

export function normalizeSession(raw: unknown): CaixaSession {
  const row = (raw as Record<string, unknown>) || {};
  return {
    _id: num(row, "_id", "_id_sessao"),
    _id_operator: num(row, "_id_operator", "_id_operador"),
    status: (str(row, "status") || "aberto") as "aberto" | "fechado",
    opening_fund: num(row, "opening_fund", "valor_fundo_inicial"),
    closing_amount: num(row, "closing_amount", "valor_fechamento") || undefined,
    opened_at: str(row, "opened_at", "data_abertura", "created_at", "criado_em"),
    closed_at: str(row, "closed_at", "data_fechamento") || undefined,
    notes: str(row, "notes", "observacoes") || undefined,
    operator: row.operator ? normalizeOperator(row.operator) : undefined,
  };
}

export function normalizeTransaction(raw: unknown): CaixaTransaction {
  const row = (raw as Record<string, unknown>) || {};
  const items = Array.isArray(row.items)
    ? row.items.map((item) => {
        const i = item as Record<string, unknown>;
        return {
          _id: num(i, "_id"),
          barcode: str(i, "barcode", "codigo_barras"),
          product_name: str(i, "product_name", "nome_produto"),
          sector: str(i, "sector", "setor"),
          unit_price: num(i, "unit_price", "valor_unitario"),
          quantity: num(i, "quantity", "quantidade") || 1,
          total_value: num(i, "total_value", "valor_total"),
        };
      })
    : [];

  const payments = Array.isArray(row.payments) ? row.payments.map(normalizePayment) : [];

  return {
    _id: num(row, "_id", "_id_transacao"),
    _id_session: num(row, "_id_session", "_id_sessao"),
    _id_employee: num(row, "_id_employee", "_id_funcionario"),
    _id_client: num(row, "_id_client", "_id_cliente") || undefined,
    client_name: str(row, "client_name", "nome_cliente"),
    consumption_date: str(row, "consumption_date", "data_consumo"),
    sector_description: str(row, "sector_description", "setor_descritivo"),
    total_value: num(row, "total_value", "valor_total"),
    paid_value: num(row, "paid_value", "valor_pago"),
    status: (str(row, "status") || "pendente") as TransactionStatus,
    items,
    payments,
  };
}

function listFrom<T>(data: unknown, keys: string[], normalizer: (raw: unknown) => T): T[] {
  const row = (data as Record<string, unknown>) || {};
  for (const key of keys) {
    const value = row[key];
    if (Array.isArray(value)) return value.map(normalizer);
  }
  if (Array.isArray(data)) return data.map(normalizer);
  if (Array.isArray(row.data)) return row.data.map(normalizer);
  return [];
}

export async function fetchCaixaDashboard(): Promise<CaixaDashboard> {
  const response = await axios.get(`${base()}/dashboard`, client("caixa/dashboard"));
  const data = response.data || {};
  return {
    open_session: data.open_session ? normalizeSession(data.open_session) : null,
    transactions_count: Number(data.transactions_count || 0),
    total_sales: Number(data.total_sales || 0),
    payments_by_method: (data.payments_by_method || []).map((p: Record<string, unknown>) => ({
      method: String(p.method || p.forma_pagamento) as PaymentMethod,
      total: Number(p.total || 0),
    })),
    sangrias_total: Number(data.sangrias_total || 0),
    suprimentos_total: Number(data.suprimentos_total || 0),
    active_operators: Number(data.active_operators || 0),
  };
}

export async function fetchOpenSession(): Promise<CaixaSession | null> {
  const response = await axios.get(`${base()}/sessoes/aberta`, client("caixa/sessions/find-open"));
  // A API responde { session: null } quando não há caixa aberto. Com
  // "session ?? data", o null caía no objeto { session: null } e virava uma
  // sessão falsa (id 0, status "aberto"). Só usa data como sessão quando a
  // resposta não traz a chave session.
  const data = response.data;
  const raw =
    data && typeof data === "object" && "session" in data ? data.session : data;
  return raw ? normalizeSession(raw) : null;
}

export async function fetchSessions(filters?: {
  page?: number;
  amount?: number;
  date_from?: string;
  date_to?: string;
}): Promise<CaixaSession[]> {
  const response = await axios.get(`${base()}/sessoes`, {
    ...client("caixa/sessions/find"),
    params: filters,
  });
  return listFrom(response.data, ["sessions", "sessoes"], normalizeSession);
}

export async function openSession(data: {
  _id_operator: number;
  opening_fund: number;
  notes?: string;
}) {
  const response = await axios.post(`${base()}/sessoes/abrir`, data, client("caixa/sessions/open"));
  return normalizeSession(response.data?.session ?? response.data);
}

export async function closeSession(_id: number, data: { closing_amount: number; notes?: string }) {
  const response = await axios.post(
    `${base()}/sessoes/${_id}/fechar`,
    data,
    client("caixa/sessions/close"),
  );
  return normalizeSession(response.data?.session ?? response.data);
}

export async function fetchOperators(search?: string): Promise<CaixaOperator[]> {
  const response = await axios.get(`${base()}/operadores`, {
    ...client("caixa/operators/find"),
    params: { page: 1, amount: 500, search: search || undefined },
  });
  return listFrom(response.data, ["operators", "operadores"], normalizeOperator);
}

export async function createOperator(data: Partial<CaixaOperator>) {
  const response = await axios.post(`${base()}/operadores`, data, client("caixa/operators/create"));
  return normalizeOperator(response.data?.operator ?? response.data);
}

export interface CaixaEmployee {
  _id: number;
  name: string;
  login: string;
}

export async function fetchEmployees(search?: string): Promise<CaixaEmployee[]> {
  const response = await axios.get(`${apiUrl()}/users/employees`, {
    ...client("users/employees/find"),
    params: { page: 1, amount: 500, search: search || undefined },
  });
  const row = response.data || {};
  const list = row.employees || row.data || row.users || [];
  return (Array.isArray(list) ? list : [])
    .map((e) => {
      const item = e as Record<string, unknown>;
      const name =
        str(item, "name", "nome") ||
        [item.first_name, item.last_name].filter(Boolean).join(" ").trim();
      return {
        _id: num(item, "_id", "_id_usuario"),
        name,
        login: str(item, "login", "email", "username"),
      };
    })
    .filter((e) => e._id && e.name);
}

/**
 * Operators (caixa_operadores) are distinct from company employees (usuarios).
 * The abertura/frente screens let the user pick an employee; this resolves the
 * matching operator, creating one linked to the employee on first use so that
 * sessions/commissions keep referencing a valid operator id.
 */
export async function ensureOperatorForEmployee(
  employee: CaixaEmployee,
): Promise<CaixaOperator> {
  const operators = await fetchOperators();
  const existing = operators.find((o) => o._id_user === employee._id);
  if (existing) return existing;
  return createOperator({
    _id_user: employee._id,
    name: employee.name,
    login: employee.login || `func-${employee._id}`,
    status: "ativo",
    commission_pct: 0,
  });
}

export async function updateOperator(_id: number, data: Partial<CaixaOperator>) {
  const response = await axios.put(
    `${base()}/operadores/${_id}`,
    data,
    client("caixa/operators/update"),
  );
  return normalizeOperator(response.data?.operator ?? response.data);
}

export async function deleteOperator(_id: number) {
  await axios.delete(`${base()}/operadores/${_id}`, client("caixa/operators/delete"));
}

export async function fetchTransactions(filters?: {
  page?: number;
  amount?: number;
  search?: string;
  date_from?: string;
  date_to?: string;
}): Promise<CaixaTransaction[]> {
  const response = await axios.get(`${base()}/transacoes`, {
    ...client("caixa/transactions/find"),
    params: filters,
  });
  return listFrom(response.data, ["transactions", "transacoes"], normalizeTransaction);
}

export async function fetchTransactionById(_id: number): Promise<CaixaTransaction> {
  const response = await axios.get(
    `${base()}/transacoes/${_id}`,
    client("caixa/transactions/find-by-id"),
  );
  return normalizeTransaction(response.data?.transaction ?? response.data);
}

export async function createTransaction(data: {
  _id_session: number;
  _id_employee: number;
  _id_client?: number;
  client_name: string;
  consumption_date?: string;
  sector_description: string;
  items: CaixaTransactionItem[];
  payments: {
    payment_method: PaymentMethod;
    amount: number;
    card_brand?: string;
    authorization_code?: string;
    fee?: number;
    transaction_number?: string;
  }[];
}) {
  const response = await axios.post(
    `${base()}/transacoes`,
    data,
    client("caixa/transactions/create"),
  );
  return normalizeTransaction(response.data?.transaction ?? response.data);
}

export async function cancelTransaction(_id: number) {
  const response = await axios.post(
    `${base()}/transacoes/${_id}/cancelar`,
    {},
    client("caixa/transactions/cancel"),
  );
  return normalizeTransaction(response.data?.transaction ?? response.data);
}

export async function fetchPayments(filters?: {
  page?: number;
  amount?: number;
  search?: string;
  date_from?: string;
  date_to?: string;
}): Promise<CaixaPayment[]> {
  const response = await axios.get(`${base()}/pagamentos`, {
    ...client("caixa/payments/find"),
    params: filters,
  });
  const row = response.data || {};
  const list = row.payments || row.pagamentos || row.data || [];
  return Array.isArray(list) ? list.map(normalizePayment) : [];
}

export async function fetchWithdrawals(filters?: {
  page?: number;
  amount?: number;
  date_from?: string;
  date_to?: string;
}): Promise<CaixaWithdrawal[]> {
  const response = await axios.get(`${base()}/sangrias`, {
    ...client("caixa/withdrawals/find"),
    params: filters,
  });
  const row = response.data || {};
  const list = row.withdrawals || row.sangrias || row.data || [];
  return (Array.isArray(list) ? list : []).map((w) => {
    const item = w as Record<string, unknown>;
    return {
      _id: num(item, "_id"),
      _id_session: num(item, "_id_session", "_id_sessao"),
      _id_operator: num(item, "_id_operator", "_id_operador"),
      type: str(item, "type", "tipo") as WithdrawalType,
      description: str(item, "description", "descricao"),
      amount: num(item, "amount", "valor"),
      movement_date: str(item, "movement_date", "data_movimentacao") || undefined,
    };
  });
}

export async function createWithdrawal(data: {
  _id_session: number;
  _id_operator: number;
  type: WithdrawalType;
  description: string;
  amount: number;
}) {
  const response = await axios.post(`${base()}/sangrias`, data, client("caixa/withdrawals/create"));
  return response.data;
}

export async function fetchBracelets(search?: string): Promise<CaixaBracelet[]> {
  const response = await axios.get(`${base()}/pulseiras`, {
    ...client("caixa/bracelets/find"),
    params: { page: 1, amount: 500, search: search || undefined },
  });
  const row = response.data || {};
  const list = row.bracelets || row.pulseiras || row.data || [];
  return (Array.isArray(list) ? list : []).map((b) => {
    const item = b as Record<string, unknown>;
    return {
      _id: num(item, "_id"),
      code: str(item, "code", "codigo"),
      _id_client: num(item, "_id_client", "_id_cliente") || undefined,
      client_name: str(item, "client_name", "nome_cliente"),
      initial_balance: num(item, "initial_balance", "saldo_inicial"),
      current_balance: num(item, "current_balance", "saldo_atual", "balance", "saldo"),
      status: str(item, "status") as BraceletStatus,
    };
  });
}

export async function createBracelet(data: {
  code: string;
  _id_client?: number;
  client_name: string;
  initial_balance: number;
}) {
  const response = await axios.post(`${base()}/pulseiras`, data, client("caixa/bracelets/create"));
  return response.data;
}

export async function updateBracelet(
  _id: number,
  data: { client_name?: string; status?: BraceletStatus },
) {
  const response = await axios.put(
    `${base()}/pulseiras/${_id}`,
    data,
    client("caixa/bracelets/update"),
  );
  return response.data;
}

export async function deleteBracelet(_id: number) {
  await axios.delete(`${base()}/pulseiras/${_id}`, client("caixa/bracelets/delete"));
}

export async function findBraceletByCode(code: string) {
  const response = await axios.get(
    `${base()}/pulseiras/${encodeURIComponent(code)}`,
    client("caixa/bracelets/find-by-code"),
  );
  return response.data;
}

export async function consumeBracelet(code: string, amount: number) {
  const response = await axios.post(
    `${base()}/pulseiras/${encodeURIComponent(code)}/consumir`,
    { amount },
    client("caixa/bracelets/consume"),
  );
  return response.data;
}

export async function fetchCommissions(filters?: {
  page?: number;
  amount?: number;
  search?: string;
  date_from?: string;
  date_to?: string;
}): Promise<CaixaCommission[]> {
  const response = await axios.get(`${base()}/comissoes`, {
    ...client("caixa/commissions/find"),
    params: filters,
  });
  const row = response.data || {};
  const list = row.commissions || row.comissoes || row.data || [];
  return (Array.isArray(list) ? list : []).map((c) => {
    const item = c as Record<string, unknown>;
    return {
      _id: num(item, "_id"),
      _id_operator: num(item, "_id_operator", "_id_operador"),
      operator_name: str(item, "operator_name", "nome_operador") || undefined,
      _id_transaction: num(item, "_id_transaction", "_id_transacao") || undefined,
      commission_pct: num(item, "commission_pct", "comissao_pct", "percentage", "percentual"),
      base_amount: num(item, "base_amount", "valor_base", "sale_value", "valor_venda"),
      commission_amount: num(item, "commission_amount", "valor_comissao", "commission_value"),
      created_at: str(item, "created_at", "criado_em") || undefined,
    };
  });
}

export async function fetchAuditLogs(filters?: {
  page?: number;
  amount?: number;
  entity?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
}): Promise<CaixaAuditLog[]> {
  const response = await axios.get(`${base()}/auditoria`, {
    ...client("caixa/audit/find"),
    params: filters,
  });
  const row = response.data || {};
  const list = row.audit_logs || row.auditoria || row.data || [];
  return (Array.isArray(list) ? list : []).map((a) => {
    const item = a as Record<string, unknown>;
    return {
      _id: num(item, "_id", "_id_auditoria"),
      entity: str(item, "entity", "entidade"),
      action: str(item, "action", "acao"),
      _id_entity: num(item, "_id_entity", "_id_entidade") || undefined,
      _id_user: num(item, "_id_user", "_id_usuario") || undefined,
      _id_operator: num(item, "_id_operator", "_id_operador") || undefined,
      description: str(item, "description", "descricao") || undefined,
      previous_data: parseJsonMaybe(item.previous_data ?? item.dados_anteriores),
      new_data: parseJsonMaybe(item.new_data ?? item.dados_novos),
      created_at: str(item, "created_at", "criado_em"),
    };
  });
}

function parseJsonMaybe(value: unknown): unknown {
  if (value === undefined || value === null || value === "") return undefined;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}
